import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { Button, Modal } from "react-bootstrap";
import Table from "../../components/Theme/Table.js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../../components/Spinner";
import { RoleContext } from "../../components/RoleContext";
import { handleExportClick } from "../../components/exportFile";
import { Checkbox, Modal as AntdModal } from "antd";
import "../../components/modal.css";

import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Add as AddIcon,
  LocalShipping as DistanceIcon
} from '@mui/icons-material';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import DeleteConfirmation from '../../components/deleteConfirmation.js';

const DistancePricing = () => {
  const printableRef = useRef(null);
  
  const [distancePricings, setDistancePricings] = useState([]);
  const [id, setId] = useState(null);
  const [loading, setLoading] = useState(true);

  const roleData = useContext(RoleContext);
  const role = JSON.parse(roleData);

  // Modal states
  const [displayAddModal, setDisplayAddModal] = useState(false);
  const [displayEditModal, setDisplayEditModal] = useState(false);
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);
  
  // Export modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  // Form data for add/edit
  const [formData, setFormData] = useState({
    min_distance: '',
    max_distance: '',
    percentage: '',
    status: 'active'
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  const getDistancePricings = async () => {
    try {
      const token = localStorage.getItem("d4uToken");
      
      const response = await axios.get('/distance-pricings', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log('API Response:', response.data);
      
      const pricingData = response.data.data || response.data || [];
      setDistancePricings(Array.isArray(pricingData) ? pricingData : []);
      setLoading(false);
    } catch (error) {
      console.error('API Error:', error);
      notifyError('Failed to fetch distance pricing rules');
      setLoading(false);
    }
  };

  // Modal handlers
  const showAddModal = () => {
    setFormData({ min_distance: '', max_distance: '', percentage: '', status: 'active' });
    setErrors({});
    setDisplayAddModal(true);
  };

  const hideAddModal = () => {
    setDisplayAddModal(false);
  };

  const showEditModal = (arg) => {
    const pricing = typeof arg === 'object' && arg !== null
      ? arg
      : distancePricings.find(p => String(p.id) === String(arg));
    if (pricing) {
      setId(pricing.id);
      setFormData({
        min_distance: pricing.min_distance,
        max_distance: pricing.max_distance,
        percentage: pricing.percentage,
        status: pricing.status
      });
      setErrors({});
      setDisplayEditModal(true);
    }
  };

  const hideEditModal = () => {
    setDisplayEditModal(false);
  };

  const showDeleteModal = (id, range) => {
    setId(id);
    setDeleteMessage(`Are you sure you want to delete pricing rule "${range}"?`);
    setDisplayConfirmationModal(true);
  };

  const hideConfirmationModal = () => {
    setDisplayConfirmationModal(false);
  };

  const submitDelete = async (id) => {
    try {
      const token = localStorage.getItem("d4uToken");
      
      await axios.delete(`/distance-pricings/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      getDistancePricings();
      setDisplayConfirmationModal(false);
      notify('Distance pricing rule deleted successfully');
    } catch (error) {
      console.error('Delete Error:', error);
      notifyError('Failed to delete distance pricing rule');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.min_distance || formData.min_distance === '') {
      newErrors.min_distance = 'Minimum distance is required';
    } else if (parseFloat(formData.min_distance) < 0) {
      newErrors.min_distance = 'Minimum distance must be positive';
    }
    
    if (!formData.max_distance || formData.max_distance === '') {
      newErrors.max_distance = 'Maximum distance is required';
    } else if (parseFloat(formData.max_distance) <= 0) {
      newErrors.max_distance = 'Maximum distance must be greater than 0';
    }
    
    if (parseFloat(formData.min_distance) >= parseFloat(formData.max_distance)) {
      newErrors.max_distance = 'Maximum distance must be greater than minimum distance';
    }
    
    if (!formData.percentage || formData.percentage === '') {
      newErrors.percentage = 'Percentage is required';
    } else if (parseFloat(formData.percentage) < 0 || parseFloat(formData.percentage) > 100) {
      newErrors.percentage = 'Percentage must be between 0 and 100';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const token = localStorage.getItem("d4uToken");
      
      console.log('Submitting form data:', formData);
      
      if (displayEditModal) {
        // Edit distance pricing
        await axios.put(`/distance-pricings/${id}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        notify('Distance pricing rule updated successfully');
        hideEditModal();
      } else {
        // Add new distance pricing
        await axios.post('/distance-pricings', formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        notify('Distance pricing rule created successfully');
        hideAddModal();
      }
      getDistancePricings();
    } catch (error) {
      console.error('Submit Error:', error);
      const errorMessage = error.response?.data?.message || 'Operation failed';
      notifyError(errorMessage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Toast notifications
  const notify = (message = "Operation completed successfully.") =>
    toast.success(message, {
      position: "top-right",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

  const notifyError = (message = "Something went wrong ...") =>
    toast.error(message, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

  useEffect(() => {
    getDistancePricings();
  }, []);

  // Table columns
  const columns = React.useMemo(
    () => [
      {
        Header: "Distance Range",
        columns: [
          {
            Header: "ID",
            accessor: "id",
          },
          {
            Header: "Min Distance (km)",
            accessor: "min_distance",
            Cell: (row) => `${row.value} km`,
          },
          {
            Header: "Max Distance (km)",
            accessor: "max_distance",
            Cell: (row) => `${row.value} km`,
          },
          {
            Header: "Percentage (%)",
            accessor: "percentage",
            Cell: (row) => `${row.value}%`,
          },
        ],
      },
      {
        Header: "Details",
        columns: [
          {
            Header: "Status",
            accessor: "status",
            disableFilters: true,
            Cell: (row) => {
              const status = row.value;
              return (
                <Chip 
                  label={status === 'active' ? 'Active' : 'Inactive'} 
                  color={status === 'active' ? 'success' : 'default'}
                  variant="filled"
                  sx={{
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                />
              );
            },
          },
          {
            Header: "Action",
            accessor: "action",
            disableFilters: true,
            Cell: (row) => (
              <div style={{ 
                display: "flex", 
                gap: "15px", 
                alignItems: 'center' 
              }}>
                <Tooltip title="Edit Distance Pricing">
                  <span>
                    <EditIcon 
                      color="primary" 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.2)',
                        }
                      }}
                      onClick={() => showEditModal(row.row.original)}
                    />
                  </span>
                </Tooltip>
                <Tooltip title="Delete Distance Pricing">
                  <span>
                    <DeleteIcon 
                      color="error" 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.2)',
                        }
                      }}
                      onClick={() => 
                        showDeleteModal(
                          row.cell.row.original.id,
                          `${row.cell.row.original.min_distance}km - ${row.cell.row.original.max_distance}km`
                        )
                      }
                    />
                  </span>
                </Tooltip>
              </div>
            ),
          },
        ],
      },
    ],
    []
  );

  // Export functionality
  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const onChange = (checkedValues) => {
    setSelectedColumns(checkedValues);
  };

  const exportColumns = [
    { label: "ID", value: "id" },
    { label: "Min Distance", value: "min_distance" },
    { label: "Max Distance", value: "max_distance" },
    { label: "Percentage", value: "percentage" },
    { label: "Status", value: "status" },
  ];

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <ToastContainer />
      
      <div style={{ padding: '20px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '20px' 
        }}>
          <h2 style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            fontWeight: 'bold',
            margin: 0 
          }}>
            <DistanceIcon fontSize="large" />
            Distance-Based Pricing
          </h2>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button 
              variant="primary"
              onClick={showAddModal}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <AddIcon /> Add Distance Pricing
            </Button>
            
            <Button 
              variant="success"
              onClick={showModal}
            >
              Export
            </Button>
          </div>
        </div>

        <div ref={printableRef}>
          <Table columns={columns} data={distancePricings} />
        </div>
      </div>

      {/* Add Modal */}
      <Modal
        style={{marginTop: "60px"}} 
        show={displayAddModal}
        onHide={hideAddModal}
        backdrop={false}
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Distance Pricing Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12">
              <div className="card card-primary">
                <div className="card-body">
                  <div className="form-group">
                    <label htmlFor="min_distance">Minimum Distance (km)</label>
                    <input
                      type="number"
                      name="min_distance"
                      value={formData.min_distance}
                      onChange={handleInputChange}
                      className={errors.min_distance ? "form-control input-error" : "form-control"}
                      step="0.1"
                      min="0"
                    />
                    {errors.min_distance && <p className="error">{errors.min_distance}</p>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="max_distance">Maximum Distance (km)</label>
                    <input
                      type="number"
                      name="max_distance"
                      value={formData.max_distance}
                      onChange={handleInputChange}
                      className={errors.max_distance ? "form-control input-error" : "form-control"}
                      step="0.1"
                      min="0"
                    />
                    {errors.max_distance && <p className="error">{errors.max_distance}</p>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="percentage">Percentage (%)</label>
                    <input
                      type="number"
                      name="percentage"
                      value={formData.percentage}
                      onChange={handleInputChange}
                      className={errors.percentage ? "form-control input-error" : "form-control"}
                      step="0.1"
                      min="0"
                      max="100"
                    />
                    {errors.percentage && <p className="error">{errors.percentage}</p>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      className="form-control custom-select"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <small className="form-text text-muted">
                      Example: If min=5km, max=10km, percentage=10%, then rides between 5-10km will have 10% added to the total price.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="default" onClick={hideAddModal}>
            Cancel
          </Button>
          <button onClick={handleSubmit} className="btn btn-success" type="button">
            Submit
          </button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal
        style={{marginTop: "60px"}} 
        show={displayEditModal}
        onHide={hideEditModal}
        backdrop={false}
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Distance Pricing Rule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12">
              <div className="card card-primary">
                <div className="card-body">
                  <div className="form-group">
                    <label htmlFor="min_distance">Minimum Distance (km)</label>
                    <input
                      type="number"
                      name="min_distance"
                      value={formData.min_distance}
                      onChange={handleInputChange}
                      className={errors.min_distance ? "form-control input-error" : "form-control"}
                      step="0.1"
                      min="0"
                    />
                    {errors.min_distance && <p className="error">{errors.min_distance}</p>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="max_distance">Maximum Distance (km)</label>
                    <input
                      type="number"
                      name="max_distance"
                      value={formData.max_distance}
                      onChange={handleInputChange}
                      className={errors.max_distance ? "form-control input-error" : "form-control"}
                      step="0.1"
                      min="0"
                    />
                    {errors.max_distance && <p className="error">{errors.max_distance}</p>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="percentage">Percentage (%)</label>
                    <input
                      type="number"
                      name="percentage"
                      value={formData.percentage}
                      onChange={handleInputChange}
                      className={errors.percentage ? "form-control input-error" : "form-control"}
                      step="0.1"
                      min="0"
                      max="100"
                    />
                    {errors.percentage && <p className="error">{errors.percentage}</p>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="status">Status</label>
                    <select
                      id="status"
                      className="form-control custom-select"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <small className="form-text text-muted">
                      Example: If min=5km, max=10km, percentage=10%, then rides between 5-10km will have 10% added to the total price.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-error" onClick={hideEditModal}>
            Cancel
          </button>
          <button onClick={handleSubmit} className="btn btn-success" type="button">
            Update
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        showModal={displayConfirmationModal}
        confirmModal={submitDelete}
        hideModal={hideConfirmationModal}
        id={id}
        message={deleteMessage}
      />

      {/* Export Modal */}
      <AntdModal
        title="Select Columns to Export"
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="export"
            variant="primary"
            onClick={() => {
              handleExportClick(
                distancePricings,
                selectedColumns,
                "DistancePricing",
                printableRef
              );
              handleOk();
            }}
          >
            Export
          </Button>,
        ]}
      >
        <Checkbox.Group
          options={exportColumns}
          onChange={onChange}
          style={{ display: "flex", flexDirection: "column" }}
        />
      </AntdModal>
    </>
  );
};

export default DistancePricing;
